import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { useEmployeeOrderDetail } from '@/hooks/useEmployeeOrder'
import { commissionTypeLabels, commissionStatusLabels } from '@/types/employee'

interface OrderDetailDrawerProps {
  orderId: number | null
  open: boolean
  onClose: () => void
}

export function OrderDetailDrawer({ orderId, open, onClose }: OrderDetailDrawerProps) {
  const { data, isLoading } = useEmployeeOrderDetail(orderId || 0, open && !!orderId)

  const statusVariant = (status: number) => {
    if (status === 1) return 'default' as const
    if (status === 2) return 'destructive' as const
    return 'secondary' as const
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[640px] overflow-y-auto sm:max-w-[640px]">
        <SheetHeader>
          <SheetTitle>订单详情</SheetTitle>
        </SheetHeader>

        {isLoading || !data ? (
          <div className="mt-6 space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            {/* 订单基础信息 */}
            <div>
              <h3 className="mb-3 text-sm font-semibold">订单信息</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">订单编号：</span>
                  {data.order.order_id}
                </div>
                <div>
                  <span className="text-muted-foreground">实付金额：</span>
                  ¥{data.order.pay_price?.toFixed(2)}
                </div>
                <div>
                  <span className="text-muted-foreground">收件人：</span>
                  {data.order.real_name}
                </div>
                <div>
                  <span className="text-muted-foreground">手机号：</span>
                  {data.order.user_phone}
                </div>
                <div className="col-span-2">
                  <span className="text-muted-foreground">地址：</span>
                  {data.order.user_address || '-'}
                </div>
                <div>
                  <span className="text-muted-foreground">下单时间：</span>
                  {data.order.add_time}
                </div>
                <div>
                  <span className="text-muted-foreground">支付时间：</span>
                  {data.order.paid_time}
                </div>
              </div>
            </div>

            <Separator />

            {/* 商品列表 */}
            {data.cart_infos && data.cart_infos.length > 0 && (
              <>
                <div>
                  <h3 className="mb-3 text-sm font-semibold">商品信息</h3>
                  <div className="rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>商品</TableHead>
                          <TableHead className="text-center">数量</TableHead>
                          <TableHead className="text-right">小计</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.cart_infos.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                {item.product_image && (
                                  <img
                                    src={item.product_image}
                                    alt={item.product_name}
                                    className="h-10 w-10 rounded object-cover"
                                  />
                                )}
                                <div>
                                  <div className="font-medium">{item.product_name}</div>
                                  {item.product_attr && (
                                    <div className="text-xs text-muted-foreground">{item.product_attr}</div>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">{item.cart_num}</TableCell>
                            <TableCell className="text-right">
                              ¥{item.total_price?.toFixed(2)}
                              {typeof item.pay_price === 'number' && item.pay_price !== item.total_price && (
                                <div className="text-xs text-muted-foreground">实付 ¥{item.pay_price.toFixed(2)}</div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                <Separator />
              </>
            )}

            {/* 佣金分配 */}
            {data.commissions && data.commissions.length > 0 && (
              <div>
                <h3 className="mb-3 text-sm font-semibold">佣金分配</h3>
                <div className="rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>员工</TableHead>
                        <TableHead>类型</TableHead>
                        <TableHead className="text-right">利润</TableHead>
                        <TableHead className="text-right">比例</TableHead>
                        <TableHead className="text-right">佣金</TableHead>
                        <TableHead className="text-right">有效佣金</TableHead>
                        <TableHead>状态</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.commissions.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="font-medium">{c.user_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {commissionTypeLabels[c.commission_type]}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">¥{c.profit.toFixed(2)}</TableCell>
                          <TableCell className="text-right">{c.rate}%</TableCell>
                          <TableCell className="text-right">¥{c.amount.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-medium">
                            ¥{c.effective_amount.toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusVariant(c.status)} className="text-xs">
                              {commissionStatusLabels[c.status]}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
